/** Session-only destinations, rechecked against the current navigation grants. */
import { useEffect, useState } from 'react';

export type HeaderPlaceRoute = { view: string; projectId?: never; tab?: never }
  | { view: 'project-detail'; projectId: string; tab: string };
export interface RecentHeaderPlace {
  id: string;
  label: string;
  description?: string;
  route: HeaderPlaceRoute;
}
interface RecentHeaderPlacesOptions {
  owner: string;
  current: RecentHeaderPlace | null;
  mainIds: readonly string[];
  projectId?: string;
  projectTabIds: readonly string[];
}

function allowed(place: RecentHeaderPlace, options: RecentHeaderPlacesOptions): boolean {
  if (place.route.projectId) {
    return options.mainIds.includes('projects') && place.route.projectId === options.projectId
      && options.projectTabIds.includes(place.route.tab);
  }
  return options.mainIds.includes(place.route.view);
}

export function useRecentHeaderPlaces(options: RecentHeaderPlacesOptions): RecentHeaderPlace[] {
  const { owner, current, mainIds, projectId, projectTabIds } = options;
  const [history, setHistory] = useState<{ owner: string; places: RecentHeaderPlace[] }>({ owner, places: [] });
  useEffect(() => {
    setHistory(previous => {
      const places = previous.owner === owner ? previous.places : [];
      if (!current || !allowed(current, { owner, current, mainIds, projectId, projectTabIds })) {
        return previous.owner === owner ? previous : { owner, places: [] };
      }
      if (places[0]?.id === current.id && places[0].label === current.label && places[0].description === current.description) return previous;
      return { owner, places: [current, ...places.filter(place => place.id !== current.id)].slice(0, 8) };
    });
  }, [owner, current, mainIds, projectId, projectTabIds]);
  useEffect(() => {
    const clear = () => setHistory({ owner, places: [] });
    window.addEventListener('quantis:auth-cleared', clear);
    return () => window.removeEventListener('quantis:auth-cleared', clear);
  }, [owner]);
  return history.owner === owner ? history.places.filter(place => allowed(place, options)).slice(0, 5) : [];
}
